import { WBFlex, WBIconButton } from '@admiin-com/ds-web';
import { Annotation, AnnotationsUnion } from '@nutrient-sdk/viewer';
import React, { forwardRef, useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';

import { PdfPlaceholderV2 } from '../PdfPlaceholder/PdfPlaceholder';
import {
  createCustomSignatureNode,
  getAnnotation,
} from '../../helpers/signature';
import { useMediaQuery, useTheme } from '@mui/material';
import AddSignatureModal from '../AddSignatureModal/AddSignatureModal';
import LoadSvgIcon from '../../component/LoadSvgIcon/LoadSvgIcon';
import StartArrowIcon from '../../../assets/icons/start-arrow.svg';
import SignedIcon from '../../../assets/icons/signed-arrow.svg';
import FinishArrowIcon from '../../../assets/icons/finish-arrow.svg';
import NextArrowIcon from '../../../assets/icons/next-arrow.svg';
import PdfSigningConfirmationDlg from '../PdfSigningConfirmationDlg/PdfSigningConfirmationDlg';
import { useTranslation } from 'react-i18next';
import { usePdfScroll } from './PdfScrollContext';
import { stripPdfIdFromAnnotations } from '../../helpers/tasks';
import { getNutrientConfig } from '../../features/hooks/useNutrientPreload/useNutrientPreload';

// if domain is local host
export interface PdfSignProps {
  documentUrl?: string;
  height?: any;
  headless?: boolean;
  annotations?: any;
  onPdfLoad?: () => void;
  onSignedAll?: (annotations: string | null) => Promise<void>;
  showSigningMessage?: boolean;
}

const renderConfigurations: Record<any, any> = {};
export const PdfSign = forwardRef(
  (
    {
      height,
      documentUrl,
      annotations,
      onPdfLoad,
      showSigningMessage = true,
    }: PdfSignProps,
    instanceRef: any
  ) => {
    const containerRef = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const pdfInstanceRef = useRef<any>(null);

    const {
      signingAnnotationRef,
      signatureName,
      setSignatureName,
      showAddSignModal,
      fullSigned,
      setShowAddSignModal,
      indicatorIndex,
      setSignatureToAll,
      signatureToAll,
      updateAnnotations,
      disableNextButton,
      loading,
      totalSignatures,
      completed,
      setActionedSignatures,
      signedSignatures,
      setSignedSignatures,
      openConfirmation,
      actionedSignatures,
      indicatorTop,
      setOpenConfirmation,
      isFinish,
      setContainerTop,
      handleMoveIndicator,
    } = usePdfScroll();

    let icon = StartArrowIcon;
    if (indicatorIndex > 0) {
      icon = NextArrowIcon;
    }
    if (isFinish) {
      icon = FinishArrowIcon;
    }
    if (completed) {
      icon = SignedIcon;
    }

    useEffect(() => {
      const container = containerRef.current; // This `useRef` instance will render the PDF.

      const getTopPosition = () => {
        if (containerRef.current) {
          const rect = (containerRef.current as any).getBoundingClientRect();
          console.log('Top Position:', rect.top); // This gives the top position of the div
          return rect.top;
        }
      };

      setContainerTop(getTopPosition());

      let NutrientViewer: any;

      if (!instanceRef) {
        instanceRef = { current: null };
      }

      const getAnnotationRenderers = ({
        annotation,
      }: {
        annotation: AnnotationsUnion;
      }) => {
        // Use cached render configuration
        if (renderConfigurations[annotation.id]) {
          return renderConfigurations[annotation.id];
        }

        const { customData } = annotation;
        const placeholderUi = <PdfPlaceholderV2 customData={customData} />;
        const customNode = createCustomSignatureNode(placeholderUi, customData);
        renderConfigurations[annotation.id] = {
          node: customNode,
          append: true,
        };
        return renderConfigurations[annotation.id] || null;
      };

      (async function () {
        NutrientViewer = await import('@nutrient-sdk/viewer'); // Load Nutrient Viewer asynchronously.
        NutrientViewer.unload(container); // unload any existing instances
        if (!documentUrl) return;
        try {
          NutrientViewer.unload(container); // Ensure that there's only one Nutrient Viewer instance.
          const nutrientParams: any = {
            container,
            document: documentUrl,
            ...getNutrientConfig(),
            disableTextSelection: true,
            customRenderers: {
              Annotation: getAnnotationRenderers,
            },
            styleSheets: ['/viewer.css'],
            initialViewState: isMobile
              ? new NutrientViewer.ViewState({ showToolbar: false })
              : new NutrientViewer.ViewState({
                  sidebarMode: NutrientViewer.SidebarMode.THUMBNAILS,
                  sidebarPlacement: NutrientViewer.SidebarPlacement.END,
                  showToolbar: false,
                }),
            inlineTextSelectionToolbarItems: () => {
              return [];
            },
          };
          if (isMobile) {
            nutrientParams.toolbarItems = [];
          }

          const annotationData = annotations
            ? JSON.parse(annotations)?.annotations
            : [];
          if (annotations) {
            nutrientParams.instantJSON = {
              ...JSON.parse(stripPdfIdFromAnnotations(annotations ?? '') ?? ''),
              annotations: annotationData.map((annotation: any) => ({
                ...annotation,
                flags:
                  annotation.customData.type === 'SIGNATURE' &&
                  annotation.customData.status === 'PENDING'
                    ? annotation.flags
                    : [
                        ...(Array.isArray(annotation.flags)
                          ? annotation.flags
                          : []),
                        'readOnly',
                      ],
              })),
            };
          }

          instanceRef.current = await NutrientViewer.load(nutrientParams);
          pdfInstanceRef.current = instanceRef.current;

          const stopAnnotationMove = (annotation: Annotation) => {
            if (annotation) {
              instanceRef.current.contentDocument.ownerDocument.addEventListener(
                'pointermove',
                stopPropagation,
                { capture: true }
              );
              instanceRef.current.contentDocument.ownerDocument.addEventListener(
                'mousemove',
                stopPropagation,
                {
                  capture: true,
                }
              );
              instanceRef.current.contentDocument.ownerDocument.addEventListener(
                'touchmove',
                stopPropagation,
                {
                  capture: true,
                }
              );

              instanceRef.current.contentDocument.ownerDocument.addEventListener(
                'keydown',
                stopPropagation,
                {
                  capture: true,
                }
              );
            } else {
              instanceRef.current.contentDocument.ownerDocument.removeEventListener(
                'pointermove',
                stopPropagation,
                { capture: true }
              );

              instanceRef.current.contentDocument.ownerDocument.removeEventListener(
                'mousemove',
                stopPropagation,
                {
                  capture: true,
                }
              );
              instanceRef.current.contentDocument.ownerDocument.removeEventListener(
                'touchmove',
                stopPropagation,
                {
                  capture: true,
                }
              );

              instanceRef.current.contentDocument.ownerDocument.removeEventListener(
                'keydown',
                stopPropagation,
                {
                  capture: true,
                }
              );
            }
          };

          const totalPages = instanceRef.current.totalPageCount;
          const allAnnotations = [];

          for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
            const annotations = await instanceRef.current.getAnnotations(
              pageIndex
            );
            allAnnotations.push(...annotations.toArray());
          }
          const filteredAnnotations = annotationData.filter(
            (annotation: any) =>
              annotation.customData?.status === 'PENDING' &&
              // annotation.isSignersAnnotation &&
              annotation.customData?.type !== 'SIGNATURE'
          );

          for (const annotation of filteredAnnotations) {
            const updatedAnnotation = await getAnnotation(
              annotation,
              annotation.customData?.signerId,
              true
            );
            await instanceRef.current.delete(annotation.id);
            await instanceRef.current.create(updatedAnnotation);
          }
          const stopPropagation = (event: any) => {
            if (event.target.getAttribute('data-testid') !== 'canvas-element')
              event.stopImmediatePropagation();
          };

          instanceRef.current.addEventListener(
            'annotationSelection.change',
            (annotation: Annotation) => {
              if (!showAddSignModal) stopAnnotationMove(annotation);
            }
          );

          // for (const annotation of realAnnotations) {
          //   if (
          //     annotation.customData?.type === 'SIGNATURE' &&
          //     annotation.customData.status === 'PENDING'
          //   )
          //     instanceRef.current.create(createSignatureWidget(annotation));
          // }
          onPdfLoad && onPdfLoad();
          // Assume `instance` is your Nutrient Viewer instance
          instanceRef?.current.addEventListener(
            'annotations.press',
            (event: any) => {
              const annotation = event.annotation;
              if (
                annotation.customData?.type === 'SIGNATURE' &&
                annotation.customData?.status === 'PENDING'
              ) {
                setShowAddSignModal(true);
                signingAnnotationRef.current = annotation.id;
                setSignatureName(annotation.customData?.name);
              }
            }
          );
        } catch (error) {
          console.error('ERROR loading PDF:', error);
          Sentry.captureException(error, {
            extra: {
              isMobile,
            },
          });
        }
      })();

      return () => {
        if (NutrientViewer) {
          NutrientViewer.unload(container);
        }
      };
    }, [documentUrl, isMobile, instanceRef]);
    const makeSignature = async (
      annotationId: string,
      signatureAttachmentId: string
    ) => {
      const annotation = JSON.parse(annotations)?.annotations.find(
        (at: any) => at.id === annotationId
      );
      const updatedAnnotation = await getAnnotation(
        annotation,
        signatureAttachmentId,
        true
      );
      Promise.all([
        await pdfInstanceRef.current.create(updatedAnnotation),
        await pdfInstanceRef.current.delete(annotationId),
      ]);
      setSignedSignatures((prev: Array<string>) => [...prev, annotationId]);
    };
    const signatureAddHandler = async (signatureBlob?: string | Blob) => {
      console.log('signatureBlob', signatureBlob);
      if (!pdfInstanceRef?.current) {
        console.error('Nutrient Viewer instance not loaded.');
        return;
      }
      setShowAddSignModal(false);
      if (signatureBlob && typeof signatureBlob !== 'string') {
        const signatureAttachmentId =
          await pdfInstanceRef.current.createAttachment(signatureBlob);

        if (!signatureToAll) {
          await makeSignature(
            signingAnnotationRef.current,
            signatureAttachmentId
          );

          setActionedSignatures((prev: number) => prev + 1);
        } else {
          const annotationData = JSON.parse(annotations)?.annotations;

          const pendingSigantures = annotationData.filter(
            (annotation: any) =>
              signedSignatures.indexOf(annotation.id) === -1 &&
              annotation.customData?.status === 'PENDING' &&
              // annotation.isSignersAnnotation &&
              annotation.customData?.type === 'SIGNATURE'
          );

          setActionedSignatures(totalSignatures);
          await Promise.all(
            pendingSigantures.map(async (annotation: any) => {
              await makeSignature(annotation.id, signatureAttachmentId);
            })
          );
        }
      }
    };
    // This div element will render the document to the DOM.
    const { t } = useTranslation();

    return (
      <>
        <WBFlex
          ref={containerRef}
          className="pdf-viewer"
          flex={1}
          {...(height ? { height } : {})}
          // sx={{ width: '100%', height: height ?? ['250px', '150px'] }}
        />

        <WBIconButton
          sx={{
            position: 'absolute',
            left: 0,
            transform: 'translateX(-100%)',
            top: indicatorTop,
            color: fullSigned ? 'success' : 'primary',
          }}
          onClick={() => {
            if (isFinish) {
              setOpenConfirmation(true);
            } else {
              handleMoveIndicator();
            }
          }}
          disabled={disableNextButton || completed}
        >
          <LoadSvgIcon component={icon} width={80} height={50} />
        </WBIconButton>
        <PdfSigningConfirmationDlg
          open={openConfirmation}
          loading={loading}
          actionedSignatures={actionedSignatures}
          totalSignatures={totalSignatures}
          onConfirm={updateAnnotations}
          onClose={() => setOpenConfirmation(false)}
          showSigningMessage={showSigningMessage}
        />
        <AddSignatureModal
          open={showAddSignModal}
          applyToAll={signatureToAll}
          onApplyToAll={(value: boolean) => setSignatureToAll(value)}
          handleClose={() => setShowAddSignModal(false)}
          handleSave={signatureAddHandler}
          isGuest
          signerName={signatureName}
          totalSignatures={totalSignatures}
        />
      </>
    );
  }
);

export default PdfSign;
